import Destination from './Destination';
import Line from '@shared/components/Line';
import styles from './Vehicle.module.scss';
import Call from './Call';
import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useElementSize from '@shared/hooks/useElementSize';
import classNames from 'classnames';
import Continuation from './Continuation';
import Clock from '@shared/components/Clock';
import { VehicleType } from '../models/vehicleType';
import Message from '@shared/components/Message';
import { Vehicle as VehicleModel } from '../models/vehicle';
import { DeepReadonly } from 'ts-essentials';
import { SCROLL_TIME_IN_MILLISECONDS } from '@shared/constants';
import { Easing, Group, Tween } from '@tweenjs/tween.js';
import { getRemPixelSize } from '@shared/utils/rem';
import useAnimationControls from '@shared/hooks/useAnimationControls';

interface VehicleProps {
  vehicleType: VehicleType;
  vehicle?: DeepReadonly<VehicleModel>;
}

export default function Vehicle({ vehicleType, vehicle }: VehicleProps) {
  const { isPaused } = useAnimationControls();
  const vehicleRef = useRef<HTMLDivElement>(null);
  const vehicleSize = useElementSize(vehicleRef);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerSize = useElementSize(headerRef);
  const availableHeight =
    vehicleSize.height !== undefined
      ? vehicleSize.height - (headerSize.height ?? 0)
      : undefined;
  const stopsMaxHeight =
    availableHeight !== undefined ? `${availableHeight}px` : undefined;
  const stopsContainerRef = useRef<HTMLDivElement>(null);
  const stopsRef = useRef<HTMLDivElement>(null);
  const [stopElementHeights, setStopElementHeights] = useState<number[]>([]);
  const [currentStopElement, setCurrentStopElement] =
    useState<HTMLDivElement | null>(null);
  const [initialStopSelectionOffset, setInitialStopSelectionOffset] =
    useState(0);
  const stopSelectionRef = useRef<HTMLDivElement>(null);
  const [stopSelectionHeight, setStopSelectionHeight] = useState<
    number | undefined
  >();
  const isLastStopSticky = vehicleType === 'train';
  const reversedCalls =
    vehicle?.journey !== undefined && vehicle.journey !== 'off-route'
      ? [...vehicle.journey.calls].reverse()
      : undefined;
  const renderStops = reversedCalls !== undefined && reversedCalls.length > 0;
  const isStopPressedWithoutStops = vehicle?.isStopPressed && !renderStops;

  const currentCallIndex = useMemo(() => {
    if (vehicle?.journey === undefined || vehicle.journey === 'off-route') {
      return undefined;
    }
    return vehicle.journey.currentCallIndex;
  }, [vehicle]);

  const currentCallStopAreaName = useMemo(() => {
    if (
      currentCallIndex === undefined ||
      vehicle?.journey === undefined ||
      vehicle.journey === 'off-route'
    ) {
      return undefined;
    }
    return vehicle.journey.calls[currentCallIndex]?.stopAreaName;
  }, [currentCallIndex, vehicle]);

  const pagination = useMemo(() => {
    if (
      !availableHeight ||
      stopElementHeights.length === 0 ||
      currentCallIndex === undefined ||
      currentCallIndex >= stopElementHeights.length
    ) {
      return undefined;
    }
    let totalHeight = 0;
    let firstElementIndex = currentCallIndex;
    let lastElementIndex = currentCallIndex;
    const elementsIndexes = [];
    // Prioritize showing future elements
    for (let i = currentCallIndex; i < stopElementHeights.length; i++) {
      elementsIndexes.push(i);
    }
    // If room for more elements, show past elements
    for (let i = currentCallIndex - 1; i >= 0; i--) {
      elementsIndexes.push(i);
    }
    const maxHeight = availableHeight - getRemPixelSize(5.3375); // Save space for approximated growth of current element when changing current index, calc((var(--current-stop-vertical-padding) - var(--stop-vertical-padding)) * 2) = 3rem for padding difference and calc(var(--H1-font-size) * var(--H1-line-height) - var(--H2-font-size) * var(--H2-line-height)) = 2.3375rem for font size difference
    for (let i = 0; i < elementsIndexes.length; i++) {
      const elementIndex = elementsIndexes[i]!;
      const height = stopElementHeights[elementIndex];
      if (height === undefined) {
        console.error(
          `Expected height for element index ${elementIndex} not found`,
        );
        return undefined;
      }
      if (totalHeight + height > maxHeight) {
        break;
      }
      totalHeight += height;
      if (elementIndex < firstElementIndex) {
        firstElementIndex = elementIndex;
      }
      if (elementIndex > lastElementIndex) {
        lastElementIndex = elementIndex;
      }
    }

    const shownElementCount = lastElementIndex - firstElementIndex + 1;
    const collapseStopIndex =
      currentCallIndex + shownElementCount - (isLastStopSticky ? 2 : 1);

    let usedHeight = 0;
    for (let i = firstElementIndex; i <= lastElementIndex; i++) {
      usedHeight += stopElementHeights[i] ?? 0;
    }
    const remainingHeight = availableHeight - usedHeight;
    const stopGap =
      shownElementCount > 1
        ? `${remainingHeight / (shownElementCount - 1)}px`
        : undefined;

    return {
      collapseStopIndex,
      stopGap,
    };
  }, [availableHeight, stopElementHeights, currentCallIndex, isLastStopSticky]);

  useLayoutEffect(() => {
    const stops = stopsRef.current;
    const stopSelectionElement = stopSelectionRef.current;
    if (
      !stops ||
      currentCallStopAreaName === undefined ||
      currentCallIndex === undefined ||
      !stopSelectionElement
    ) {
      setCurrentStopElement(null);
      return;
    }

    const currentCallId = getId(currentCallStopAreaName, currentCallIndex);
    const newCurrentStopElement = stops.querySelector<HTMLDivElement>(
      `[data-id="${currentCallId}"]`,
    );
    const newStopSelectionOffset = newCurrentStopElement
      ? stopSelectionElement.offsetTop +
        stopSelectionElement.offsetHeight -
        (newCurrentStopElement.offsetTop + newCurrentStopElement.offsetHeight)
      : 0;
    setInitialStopSelectionOffset(newStopSelectionOffset);
    setCurrentStopElement(newCurrentStopElement);
  }, [currentCallStopAreaName, currentCallIndex]);

  useLayoutEffect(() => {
    if (!currentStopElement) {
      setStopSelectionHeight(undefined);
      return;
    }

    function updateStopSelectionHeight(): void {
      if (!currentStopElement) {
        setStopSelectionHeight(undefined);
        return;
      }

      setStopSelectionHeight(currentStopElement.offsetHeight);
    }

    updateStopSelectionHeight();

    const resizeObserver = new ResizeObserver(updateStopSelectionHeight);
    resizeObserver.observe(currentStopElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [currentStopElement]);

  useEffect(() => {
    let frameId: number;

    const group = new Group();
    const offset = { value: initialStopSelectionOffset };
    const tween = new Tween(offset)
      .to({ value: 0 }, isPaused ? 0 : SCROLL_TIME_IN_MILLISECONDS)
      .easing(Easing.Quadratic.Out)
      .onComplete(() => {
        animating = false;
      })
      .start();
    group.add(tween);

    let animating = true;
    function update(time: number) {
      if (animating) {
        group.update(time);
      }

      if (
        currentStopElement &&
        stopSelectionRef.current &&
        stopsContainerRef.current &&
        availableHeight !== undefined
      ) {
        const stopSelectionTop =
          currentStopElement.offsetTop +
          currentStopElement.offsetHeight -
          stopSelectionRef.current.offsetHeight +
          offset.value;
        stopSelectionRef.current.style.top = `${stopSelectionTop}px`;
        stopsContainerRef.current.scrollTop =
          stopSelectionTop +
          stopSelectionRef.current.offsetHeight -
          availableHeight;
      }

      frameId = requestAnimationFrame(update);
    }

    frameId = requestAnimationFrame(update);

    return () => cancelAnimationFrame(frameId);
  }, [
    availableHeight,
    initialStopSelectionOffset,
    currentStopElement,
    isPaused,
  ]);

  function getId(stopAreaName: string | undefined, callIndex: number): string {
    return `${callIndex}-${stopAreaName}`;
  }

  useEffect(() => {
    function getStopElements(): HTMLDivElement[] | undefined {
      if (!stopsRef.current) {
        return undefined;
      }

      return [...stopsRef.current.children]
        .filter(
          (child) => window.getComputedStyle(child).position !== 'absolute',
        )
        .map((element) => element as HTMLDivElement);
    }

    function getStopElementHeights(): number[] {
      const stopElements = getStopElements();
      if (!stopElements || stopElements.length === 0) {
        return [];
      }

      // stopElements are in DOM order (reversed call order)
      // Convert to call-index order
      const heights: number[] = [];
      stopElements.forEach((element, domIndex) => {
        const callIndex = stopElements.length - 1 - domIndex;
        let totalHeight = element.clientHeight;
        const marginBottom = window.getComputedStyle(element).marginBottom;
        if (marginBottom) {
          totalHeight += parseFloat(marginBottom);
        }
        heights[callIndex] = totalHeight;
      });
      return heights;
    }

    function updateStopElementsHeights() {
      const heights = getStopElementHeights();
      setStopElementHeights(heights);
    }

    const stopElements = getStopElements();

    if (!stopsRef.current || !stopElements) {
      return;
    }

    updateStopElementsHeights();

    const resizeObserver = new ResizeObserver(updateStopElementsHeights);
    for (const stopElement of stopElements) {
      resizeObserver.observe(stopElement);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [renderStops, vehicle?.journey]);

  useEffect(() => {
    function getCallVisual(stopElement: HTMLDivElement): HTMLDivElement {
      return stopElement.children[0] as HTMLDivElement;
    }

    function getCallElements(): HTMLDivElement[] | undefined {
      if (!stopsRef.current) {
        return undefined;
      }

      return [...stopsRef.current.children]
        .filter((child) => child.hasAttribute('data-id'))
        .map((element) => element as HTMLDivElement);
    }

    function updateCallVisualPathLengths(): void {
      const stopElements = getCallElements();
      if (!stopElements) {
        return;
      }

      stopElements.forEach((stopElement, index) => {
        const previousStopElement = stopElements[index - 1];
        if (!previousStopElement) {
          return;
        }
        const pathElement = getCallVisual(stopElement);
        const pathElementTop = pathElement.offsetTop;
        const previousPathElement = getCallVisual(previousStopElement);
        const previousPathElementBottom =
          previousPathElement.offsetTop + previousPathElement.offsetHeight;
        const pathLength = pathElementTop - previousPathElementBottom;
        pathElement.style.setProperty('--path-length', `${pathLength}px`);
      });
    }

    const callElements = getCallElements();
    const stops = stopsRef.current;

    if (!stops || !callElements) {
      return;
    }
    updateCallVisualPathLengths();

    const resizeObserver = new ResizeObserver(updateCallVisualPathLengths);
    resizeObserver.observe(stops);
    for (const callElement of callElements) {
      resizeObserver.observe(callElement);
      resizeObserver.observe(getCallVisual(callElement));
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [renderStops, vehicle?.journey]);

  return (
    <div ref={vehicleRef} className={styles.vehicle}>
      {vehicleType === 'other' && (
        <div
          ref={headerRef}
          className={classNames(
            styles.header,
            !vehicle?.line && styles['header--no-line'],
          )}
        >
          {vehicle?.line && <Line line={vehicle.line} size="large" />}
          {vehicle?.destination && (
            <Destination destination={vehicle.destination} />
          )}
          <div className={styles.clock}>
            <Clock />
          </div>
        </div>
      )}
      {renderStops &&
        vehicle?.journey !== undefined &&
        vehicle.journey !== 'off-route' && (
          <div
            ref={stopsContainerRef}
            className={styles['stops-container']}
            style={{
              height: stopsMaxHeight,
            }}
          >
            <div
              ref={stopsRef}
              className={styles.stops}
              style={
                {
                  '--stop-gap': pagination?.stopGap,
                } as CSSProperties
              }
            >
              {vehicle?.journey.continuesAs && (
                <Continuation continuation={vehicle.journey.continuesAs} />
              )}
              {reversedCalls.map((call, reversedCallIndex) => {
                if (
                  vehicle.journey === undefined ||
                  vehicle.journey === 'off-route'
                ) {
                  return;
                }
                const callIndex = reversedCalls.length - reversedCallIndex - 1;
                const callId = getId(call.stopAreaName, callIndex);
                const isCurrent = callIndex === currentCallIndex;
                const isLast = callIndex === reversedCalls.length - 1;
                const isPrevious =
                  currentCallIndex !== undefined &&
                  callIndex === currentCallIndex - 1;
                const isPassed =
                  currentCallIndex !== undefined &&
                  callIndex < currentCallIndex;
                return (
                  <Call
                    key={callId}
                    id={callId}
                    vehicleType={vehicleType}
                    call={call}
                    isCurrent={isCurrent}
                    isStopPressed={vehicle.isStopPressed}
                    isAtCollapse={pagination?.collapseStopIndex === callIndex}
                    isPrevious={isPrevious}
                    isPassed={isPassed}
                    isSecondLast={callIndex === reversedCalls.length - 2}
                    isLast={isLast}
                    isMovingTowardsNextStop={
                      vehicle.journey.isMovingTowardsNextStop
                    }
                    isSticky={isLast && isLastStopSticky}
                  />
                );
              })}
              <div
                ref={stopSelectionRef}
                className={classNames(
                  styles['stop-selection'],
                  vehicle.isStopPressed &&
                    styles['stop-selection--is-stopping'],
                )}
                style={{
                  height: stopSelectionHeight,
                }}
              ></div>
            </div>
          </div>
        )}
      {!vehicle && <Message title="Vi kan inte visa något här just nu" />}
      {vehicle?.journey === 'off-route' && (
        <div className={styles['off-route']}>
          Just nu kör vi en annan väg än vanligt
        </div>
      )}
      {isStopPressedWithoutStops && (
        <div className={styles['no-stops-stopping']}>Stannar</div>
      )}
    </div>
  );
}
